import React, { useState, useEffect, Suspense } from "react";
import { Routes, Route, Navigate, useLocation, useParams, Outlet } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import HeroSection from "./HeroSection";
import WaveDivider from "./WaveDivider";
import CountryNudgeBanner from "./components/CountryNudgeBanner";
import LangSuggestionBanner from "./components/LangSuggestionBanner";
import ReactGA from "react-ga4";
import CookieConsentBanner from "./components/CookieConsentBanner";
import { hasAnalyticsConsent } from "./consent";
import SEO from "./SEO";
import { fonts, colors, radii, shadows, layout, gradients, globalKeyframes } from "./designTokens";
import { resetIOSScrollViewport } from "./utils/resetIOSViewport";
import { useTranslation } from "react-i18next";
import useIsMobile from "./hooks/useIsMobile";
import UiGlobalStyles from "./components/ui/UiGlobalStyles";
import NotFound from "./NotFound";
import AdBanner from "./components/AdBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import UploadFab from "./components/UploadFab";
import PauseWhenOffscreen from "./components/ui/PauseWhenOffscreen";
import { NON_EN_LANGS, splitLangPrefix, buildLocalizedPath } from "./utils/langRouting";

// LAZY LOAD – alle route-only Komponenten
const FartGallery    = React.lazy(() => import("./FartGallery"));
const BestFartSounds = React.lazy(() => import("./BestFartSounds"));
const UserGallery    = React.lazy(() => import("./components/UserGallery"));
const FartWave       = React.lazy(() => import("./FartWave"));
const FartPage       = React.lazy(() => import("./FartPage"));
const AdminPanel     = React.lazy(() => import("./AdminPanel"));
const ModeratorPanel = React.lazy(() => import("./ModeratorPanel"));
const PrivacyPolicy  = React.lazy(() => import("./PrivacyPolicy"));
const Impressum      = React.lazy(() => import("./Impressum"));
const UserProfile    = React.lazy(() => import("./components/UserProfile"));
const UserRegister   = React.lazy(() => import("./components/UserRegister"));
const UserLogin      = React.lazy(() => import("./components/UserLogin"));
const FAQ            = React.lazy(() => import("./FAQ"));
const ContactPage    = React.lazy(() => import("./ContactPage"));
const ForgotPassword = React.lazy(() => import("./components/ForgotPassword"));
const ResetPassword  = React.lazy(() => import("./components/ResetPassword"));
const VerifyEmail    = React.lazy(() => import("./components/VerifyEmail"));
const VerifyEmailChange = React.lazy(() => import("./components/VerifyEmailChange"));
const FeedPage       = React.lazy(() => import("./FeedPage"));
const EncyclopediaHome    = React.lazy(() => import("./EncyclopediaHome"));
const EncyclopediaArticle = React.lazy(() => import("./EncyclopediaArticle"));
const ToolsHub     = React.lazy(() => import("./ToolsHub"));
const FoodChecker  = React.lazy(() => import("./FoodChecker"));
const SymptomDiary = React.lazy(() => import("./SymptomDiary"));
const FartUpload     = React.lazy(() => import("./FartUpload"));
const FartPlayer     = React.lazy(() => import("./FartPlayer"));
const FartBomb       = React.lazy(() => import("./FartBomb"));
const FartMap        = React.lazy(() => import("./FartMap"));
const FartSynthesizer = React.lazy(() => import("./FartSynthesizer"));
const FartGame        = React.lazy(() => import("./FartGame"));
const FartInvaders    = React.lazy(() => import("./FartInvaders"));

// Startseite: Bereiche unterhalb des Hero-Bilds als eigene Chunks. Sie laden sofort
// parallel nach dem Hauptbundle (kein Warten aufs Scrollen), halten aber das
// Startbundle klein → Hero/LCP wird früher gezeichnet. Enthält u. a. die komplette
// Enzyklopädie-Liste (via EncyclopediaTeaser), die sonst im Hauptbundle landete.
const FartOfTheDay       = React.lazy(() => import("./FartOfTheDay"));
const FartBattle         = React.lazy(() => import("./FartBattle"));
const FartFunFact        = React.lazy(() => import("./FartFunFact"));
const HowTo              = React.lazy(() => import("./HowTo"));
const TrendingFarts      = React.lazy(() => import("./components/TrendingFarts"));
const EncyclopediaTeaser = React.lazy(() => import("./components/EncyclopediaTeaser"));
const CommunityJoinCTA   = React.lazy(() => import("./components/CommunityJoinCTA"));

// Platzhalter reserviert ungefähr die spätere Höhe → kein Layout-Sprung beim Nachladen
function SectionFallback({ minHeight }) {
  return <div aria-hidden="true" style={{ minHeight }} />;
}

const TRACKING_ID = "G-5SSWL5JQ0Y";

// Scroll to top on every route change (but not for hash-links like /#gallery)
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (hash) return;
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    return resetIOSScrollViewport();
  }, [pathname, hash]);
  return null;
}


function initGA() {
  if (window.__gaInitialized) return;
  ReactGA.initialize(TRACKING_ID);
  window.__gaInitialized = true;
}


function usePageTracking() {
  const location = useLocation();
  useEffect(() => {
    if (hasAnalyticsConsent() && window.__gaInitialized) {
      ReactGA.send({
        hitType: "pageview",
        // Query string deliberately excluded — reset/verify-email links carry
        // single-use secret tokens that must never reach a third party.
        page: location.pathname,
      });
    }
  }, [location]);
}

function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 320);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (!visible) return null;
  return (
    <button
      type="button"
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      aria-label="Back to top"
      style={{
        position: "fixed",
        bottom: 28,
        right: 22,
        zIndex: 999,
        width: 44,
        height: 44,
        borderRadius: "50%",
        background: "linear-gradient(135deg, #4caf50, #28c76f)",
        color: "#0a3317",
        border: "none",
        cursor: "pointer",
        fontSize: 20,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 16px rgba(76,175,80,0.45)",
        transition: "transform 0.15s, box-shadow 0.15s",
        lineHeight: 1,
      }}
      onMouseOver={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(76,175,80,0.55)"; }}
      onMouseOut={(e)  => { e.currentTarget.style.transform = "translateY(0)";    e.currentTarget.style.boxShadow = "0 4px 16px rgba(76,175,80,0.45)"; }}
    >
      ↑
    </button>
  );
}

// Liest das optionale /:lang-Segment aus der URL, synchronisiert i18next darauf
// (URL ist die alleinige Quelle der Wahrheit) und rendert die passenden
// verschachtelten Routen. Ungültige Sprachcodes (z.B. /xx/gallery) fallen
// bewusst auf NotFound statt auf die Startseite — ohne diese Prüfung würde
// React Router ein einzelnes unbekanntes Segment wie /randomtypo sonst als
// lang="randomtypo" auf die Index-Route matchen (empirisch verifiziert).
//
// WICHTIG: Hier wird NIE automatisch die URL gewechselt. Ein früherer
// Auto-Redirect von der unpräfixierten URL auf die Browsersprache
// (navigate(..., {replace:true})) hat dazu geführt, dass der Googlebot beim
// Rendern eine client-seitige Weiterleitung gesehen und ~136 Enzyklopädie-URLs
// als "Seite mit Weiterleitung" aus dem Index geworfen hat. Sprachwechsel läuft
// jetzt ausschließlich über einen Hinweis-Banner (LangSuggestionBanner) bzw. den
// Sprachschalter im Header — beide behalten indexierbare Content-URLs unangetastet.
function LangLayout() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const isValid = lang === undefined || NON_EN_LANGS.includes(lang);

  useEffect(() => {
    if (!isValid) return;

    const target = lang || "en";
    if (i18n.language !== target) i18n.changeLanguage(target);
    if (typeof document !== "undefined") document.documentElement.lang = target;
  }, [lang, isValid]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isValid) return <NotFound />;
  return <Outlet />;
}

// /science(/:slug) ist ein Altpfad — leitet lang-bewusst auf /encyclopedia um
// (declaratives <Navigate> hat kein eigenes lang-Bewusstsein, daher hier
// explizit aus der aktuellen URL abgeleitet statt statisch hartcodiert).
function RedirectToEncyclopedia() {
  const { pathname } = useLocation();
  const { lang } = splitLangPrefix(pathname);
  return <Navigate to={buildLocalizedPath(lang, "/encyclopedia")} replace />;
}

function App() {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { t } = useTranslation();
  const { basePath } = splitLangPrefix(location.pathname);
  const isProfilePage = basePath === "/profile" || basePath.startsWith("/user/") || basePath === "/player" || basePath === "/bomb" || basePath === "/map";

  // Initialize GA on mount if consent already given (returning visitor)
  useEffect(() => {
    if (hasAnalyticsConsent()) {
      initGA();
    }
  }, []);

  usePageTracking();

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: isProfilePage ? "transparent" : gradients.appBg,
        fontFamily: fonts.body,
      }}
    >
      <ScrollToTop />
      {/* Skip-to-content — nur sichtbar beim Fokus (Keyboard/Screen Reader) */}
      <a
        href="#main-content"
        style={{
          position: "absolute", top: -50, left: 8, zIndex: 9999,
          background: "#fff", color: "#1a6e13", fontWeight: 700,
          padding: "8px 16px", borderRadius: "0 0 8px 8px",
          border: "2px solid #1a6e13", textDecoration: "none", fontSize: 14,
          transition: "top 0.15s",
        }}
        onFocus={(e) => { e.currentTarget.style.top = "0"; }}
        onBlur={(e)  => { e.currentTarget.style.top = "-50px"; }}
      >
        Zum Inhalt springen
      </a>
      <Header />
      <LangSuggestionBanner />

      {basePath === "/" && (
        <>
          <PauseWhenOffscreen><HeroSection /></PauseWhenOffscreen>
          <WaveDivider />
        </>
      )}

      <CountryNudgeBanner />

      <main
        id="main-content"
        style={{
          maxWidth: isProfilePage ? "none" : layout.contentMaxWidth,
          width: "100%",
          margin: isMobile ? "0" : "0 auto",
          background: isProfilePage ? "transparent" : colors.whiteOverlay,
          borderRadius: isProfilePage ? 0 : (isMobile ? 0 : radii.card),
          padding: isProfilePage ? 0 : (isMobile ? 8 : 32),
          paddingBottom: isProfilePage ? 0 : (isMobile ? 8 : 32),
          boxShadow: isProfilePage ? "none" : (isMobile ? "none" : shadows.mainContent),
          flex: "1 0 auto",
          position: "relative",
          overflow: "visible",
          overflowX: "hidden",
          boxSizing: "border-box",
        }}
      >
        <Suspense fallback={<div style={{ textAlign: "center", padding: 48, fontSize: 28 }}>💨</div>}>
        <ErrorBoundary resetKey={location.pathname}>
        <Routes>
          <Route path="/:lang?" element={<LangLayout />}>
          <Route
            index
            element={
              <>
                <SEO
                  title={t("app.title")}
                  description={t("seo.home.description")}
                  ogUrl="https://www.art-of-fart.com/"
                />

                {/* 1 — Furz des Tages: sofort Content */}
                <section style={{ padding: isMobile ? "8px 0" : "16px 0" }}>
                  <PauseWhenOffscreen><Suspense fallback={<SectionFallback minHeight={isMobile ? 420 : 360} />}><FartOfTheDay /></Suspense></PauseWhenOffscreen>
                </section>

                <div style={{ height: 1, background: gradients.dividerGold, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* 2 — Gallery Preview: Wert der Plattform zeigen */}
                <section id="gallery" style={{ padding: isMobile ? "12px 0" : "24px 0", scrollMarginTop: 80 }}>
                  <ErrorBoundary>
                    <Suspense fallback={<div style={{ textAlign: "center", padding: 16, fontSize: 18 }}>💨 Loading gallery...</div>}>
                      <FartGallery preview />
                    </Suspense>
                  </ErrorBoundary>
                </section>

                <div style={{ height: 1, background: gradients.dividerGreen, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* 3 — Trending: "Top dieser Woche" */}
                <section style={{ padding: isMobile ? "8px 0" : "16px 0" }}>
                  <PauseWhenOffscreen><Suspense fallback={<SectionFallback minHeight={isMobile ? 520 : 420} />}><TrendingFarts /></Suspense></PauseWhenOffscreen>
                </section>

                {/* 4 — HowTo: Orientierung bevor die interaktiven Features kommen */}
                <section style={{
                  background: isMobile ? "transparent" : gradients.sectionGreen,
                  borderRadius: isMobile ? 0 : radii.md,
                  padding: isMobile ? "8px 0" : "8px 16px 16px 16px",
                  margin: isMobile ? "0 0 6px 0" : "0 -16px 16px -16px",
                }}>
                  <Suspense fallback={<SectionFallback minHeight={isMobile ? 480 : 300} />}><HowTo /></Suspense>
                </section>

                <div style={{ height: 1, background: gradients.dividerGold, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* 5 — Fart Battle: emotionaler Hook */}
                <section style={{ padding: isMobile ? "8px 0" : "16px 0" }}>
                  <PauseWhenOffscreen><Suspense fallback={<SectionFallback minHeight={isMobile ? 640 : 520} />}><FartBattle /></Suspense></PauseWhenOffscreen>
                </section>

                <div style={{ height: 1, background: gradients.dividerGold, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* 4 — Upload: jetzt hat der User Kontext und Motivation */}
                <section id="upload" style={{ padding: isMobile ? "12px 0" : "24px 0", scrollMarginTop: 80 }}>
                  <Suspense fallback={<div style={{ textAlign: "center", padding: 32, fontSize: 22 }}>🎙️</div>}>
                    <FartUpload />
                  </Suspense>
                </section>

                <div style={{ height: 1, background: gradients.dividerBlue, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* Ad — zwischen Upload und User-Champions */}
                <AdBanner slot="" style={{ margin: isMobile ? "8px 0" : "16px 0" }} />

                {/* 5 — User Champions: Community-Gefühl */}
                <section id="users" style={{ padding: isMobile ? "12px 0" : "24px 0" }}>
                  <ErrorBoundary>
                    <Suspense fallback={<div style={{ textAlign: "center", padding: 16, fontSize: 18 }}>👥 Loading users...</div>}>
                      <UserGallery preview />
                    </Suspense>
                  </ErrorBoundary>
                </section>

                <div style={{ height: 1, background: gradients.dividerGreen, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* 7 — Encyclopedia Teaser: Wissensbereich promoten */}
                <section style={{ padding: isMobile ? "8px 0" : "16px 0" }}>
                  <Suspense fallback={<SectionFallback minHeight={isMobile ? 520 : 360} />}><EncyclopediaTeaser /></Suspense>
                </section>

                <div style={{ height: 1, background: gradients.dividerBlue, margin: isMobile ? "8px 20%" : "16px 10%" }} />

                {/* 8 — Community Join CTA: Registrierung für Gäste */}
                <section style={{ padding: isMobile ? "8px 0" : "16px 0" }}>
                  <Suspense fallback={<SectionFallback minHeight={220} />}><CommunityJoinCTA /></Suspense>
                </section>

                {/* 9 — FunFact: leichter Abschluss */}
                <section style={{ padding: isMobile ? "6px 0" : "16px 0" }}>
                  <Suspense fallback={<SectionFallback minHeight={200} />}><FartFunFact /></Suspense>
                </section>
              </>
            }
          />

          <Route
            path="register"
            element={
              <>
                <SEO
                  title="Sign up – Art-of-Fart.com"
                  description="Create your free Art-of-Fart.com account."
                  ogUrl="https://www.art-of-fart.com/register"
                  noindex={true}
                />
                <UserRegister />
              </>
            }
          />

          <Route
            path="login"
            element={
              <>
                <SEO
                  title="Login – Art-of-Fart.com"
                  description="Log in to your account."
                  ogUrl="https://www.art-of-fart.com/login"
                  noindex={true}
                />
                <UserLogin />
              </>
            }
          />

          <Route
            path="forgot-password"
            element={
              <>
                <SEO
                  title="Forgot Password – Art-of-Fart.com"
                  description="Reset your Art-of-Fart.com password."
                  ogUrl="https://www.art-of-fart.com/forgot-password"
                  noindex={true}
                />
                <ForgotPassword />
              </>
            }
          />

          <Route
            path="reset-password"
            element={
              <>
                <SEO
                  title="Reset Password – Art-of-Fart.com"
                  description="Set a new password for your account."
                  ogUrl="https://www.art-of-fart.com/reset-password"
                  noindex={true}
                />
                <ResetPassword />
              </>
            }
          />

          <Route
            path="verify-email"
            element={
              <>
                <SEO
                  title="E-Mail bestätigen – Art-of-Fart.com"
                  description="Bestätige deine E-Mail-Adresse."
                  ogUrl="https://www.art-of-fart.com/verify-email"
                  noindex={true}
                />
                <VerifyEmail />
              </>
            }
          />

          <Route
            path="verify-email-change"
            element={
              <>
                <SEO
                  title="Neue E-Mail bestätigen – Art-of-Fart.com"
                  description="Bestätige deine neue E-Mail-Adresse."
                  ogUrl="https://www.art-of-fart.com/verify-email-change"
                  noindex={true}
                />
                <VerifyEmailChange />
              </>
            }
          />

          <Route
            path="profile"
            element={
              <>
                <SEO
                  title="My Profile – Art-of-Fart.com"
                  description="Manage your account."
                  ogUrl="https://www.art-of-fart.com/profile"
                  noindex={true}
                />
                <UserProfile />
              </>
            }
          />

          <Route path="science" element={<RedirectToEncyclopedia />} />
          <Route path="science/:slug" element={<RedirectToEncyclopedia />} />
          <Route path="encyclopedia" element={<EncyclopediaHome />} />
          <Route path="encyclopedia/:slug" element={<EncyclopediaArticle />} />
          <Route path="tools" element={<ToolsHub />} />
          <Route path="tools/food-checker" element={<FoodChecker />} />
          <Route path="tools/diary" element={<SymptomDiary />} />

          <Route
            path="feed"
            element={
              <>
                <SEO title={t("feed.title")} noindex={true} />
                <FeedPage />
              </>
            }
          />

          <Route
            path="gallery"
            element={
              <>
                <SEO
                  title={t("gallery.title")}
                  description={t("seo.gallery.description")}
                  ogUrl="https://www.art-of-fart.com/gallery"
                />
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <div style={{ textAlign: "center", padding: 16 }}>
                        💨 Loading gallery...
                      </div>
                    }
                  >
                    <FartGallery />
                  </Suspense>
                </ErrorBoundary>
              </>
            }
          />

          <Route
            path="fart/:id"
            element={
              <ErrorBoundary>
                <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 40 }}>💨</div>}>
                  <FartPage />
                </Suspense>
              </ErrorBoundary>
            }
          />

          <Route
            path="users"
            element={
              <>
                <SEO
                  title={t("seo.users.title")}
                  description={t("seo.users.description")}
                  ogUrl="https://www.art-of-fart.com/users"
                />
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <div style={{ textAlign: "center", padding: 16 }}>
                        Loading…
                      </div>
                    }
                  >
                    <UserGallery />
                  </Suspense>
                </ErrorBoundary>
              </>
            }
          />

          <Route
            path="user/:username"
            element={<UserProfile />}
          />

          <Route path="battle" element={<><SEO title={t("seo.battle.title")} description={t("seo.battle.description")} ogUrl="https://www.art-of-fart.com/battle" /><FartBattle fullPage /></>} />

          <Route
            path="best-fart-sounds"
            element={
              <>
                <SEO
                  title={t("seo.bestFartSounds.title")}
                  description={t("seo.bestFartSounds.description")}
                  ogUrl="https://www.art-of-fart.com/best-fart-sounds"
                />
                <ErrorBoundary>
                  <Suspense
                    fallback={
                      <div style={{ textAlign: "center", padding: 16 }}>
                        💨 Loading rankings...
                      </div>
                    }
                  >
                    <BestFartSounds />
                  </Suspense>
                </ErrorBoundary>
              </>
            }
          />

          <Route path="admin" element={<><SEO title="Admin" noindex={true} /><AdminPanel /></>} />

          <Route path="moderator" element={<><SEO title="Mod Panel" noindex={true} /><ModeratorPanel /></>} />

          <Route
            path="impressum"
            element={
              <>
                <SEO title="Impressum – Art-of-Fart.com" description="Rechtliche Angaben und Betreiberinformationen für Art-of-Fart.com." ogUrl="https://www.art-of-fart.com/impressum" />
                <Impressum />
              </>
            }
          />

          <Route
            path="privacy"
            element={
              <>
                <SEO title="Privacy Policy – Art-of-Fart.com" description="Privacy policy and data protection information for Art-of-Fart.com." ogUrl="https://www.art-of-fart.com/privacy" />
                <PrivacyPolicy />
              </>
            }
          />

          <Route
            path="faq"
            element={<FAQ />}
          />

          <Route
            path="contact"
            element={<ContactPage />}
          />

          <Route
            path="wave"
            element={
              <>
                <SEO title="FartWave Studio" noindex={true} />
                <ErrorBoundary>
                  <Suspense fallback={<div style={{ textAlign: "center", padding: 32, fontSize: 18 }}>🌊 Loading...</div>}>
                    <FartWave />
                  </Suspense>
                </ErrorBoundary>
              </>
            }
          />

          <Route path="player" element={
            <>
              <SEO title={t("player.seo.title")} description={t("player.seo.description")} ogUrl="https://www.art-of-fart.com/player" />
              <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 18 }}>💨 Loading...</div>}>
                <FartPlayer />
              </Suspense>
            </>
          } />

          <Route path="bomb" element={
            <>
              <SEO title={t("bomb.seo.title")} description={t("bomb.seo.description")} ogUrl="https://www.art-of-fart.com/bomb" />
              <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 18 }}>💣 Loading...</div>}>
                <FartBomb />
              </Suspense>
            </>
          } />

          <Route path="map" element={
            <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 28 }}>🌍</div>}>
              <FartMap />
            </Suspense>
          } />

          <Route path="synthesizer" element={
            <>
              <SEO title="Furz-Synthesizer" noindex={true} />
              <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 28 }}>🎛️</div>}>
                <FartSynthesizer />
              </Suspense>
            </>
          } />

          <Route path="fart-runner" element={
            <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 28 }}>🕹️</div>}>
              <FartGame />
            </Suspense>
          } />

          <Route path="fart-invaders" element={
            <Suspense fallback={<div style={{ textAlign: "center", padding: 60, fontSize: 28 }}>👾</div>}>
              <FartInvaders />
            </Suspense>
          } />

          <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        </ErrorBoundary>
        </Suspense>
      </main>

      <Footer />
      <BackToTop />
      {basePath === "/" && <UploadFab />}

      <style>{globalKeyframes}</style>
      <UiGlobalStyles />

      <CookieConsentBanner onAnalyticsAccepted={initGA} />
    </div>
  );
}

export default App;